import { useCallback, useEffect, useState } from 'react';
import { firstValueFrom } from 'rxjs';

import { supportedLocales } from '../components/LocaleSelector/supportedLocales';
import { DEFAULT_LOCALE } from '../contexts/IntlContext';
import { useMixpanel } from '../contexts/MixpanelContext';
import { useWLOrgConfigContext } from '../providers/WLOrgConfigProvider';
import { MixpanelMetadata } from '../tokens/mixpanel';
import { logger } from '../utils';

/** Internal ids for which app-owned JSON glob we load (paths + logging). */
const WhitelabelIntlCatalog = {
  Web: 'whitelabel',
  Mobile: 'whitelabel-mobile',
} as const;
type WhitelabelIntlCatalogId = (typeof WhitelabelIntlCatalog)[keyof typeof WhitelabelIntlCatalog];

export type WhitelabelTranslationModules = Record<string, () => Promise<{ default: Record<string, string> }>>;

/** Matches each app’s `import.meta.glob('../translations/<catalogId>.*.json')` keys. */
const WHITELABEL_INTL_TRANSLATION_MODULE_KEY: Record<WhitelabelIntlCatalogId, (locale: string) => string> = {
  [WhitelabelIntlCatalog.Web]: locale => `../translations/${WhitelabelIntlCatalog.Web}.${locale}.json`,
  [WhitelabelIntlCatalog.Mobile]: locale => `../translations/${WhitelabelIntlCatalog.Mobile}.${locale}.json`,
};

/**
 * Org-driven locale, Mixpanel locale registration, and discourage-translation flag (shared by web + mobile Intl wrappers).
 */
export function useWhitelabelOrgLocalePreferences() {
  const { loadedConfig } = useWLOrgConfigContext();
  const browserLocale = typeof window !== 'undefined' ? window.navigator.language : DEFAULT_LOCALE;
  const mixpanel = useMixpanel();

  const [locale, setLocale] = useState<string | undefined>(undefined);
  const [discourageLanguageTranslation, setDiscourageLanguageTranslation] = useState(false);

  useEffect(() => {
    loadWLDiscourageLanguageTranslation().then(setDiscourageLanguageTranslation);

    async function loadWLDiscourageLanguageTranslation() {
      return firstValueFrom(loadedConfig).then(config => {
        return config.discourageLanguageTranslation ?? false;
      });
    }
  }, [loadedConfig]);

  useEffect(() => {
    loadWLConfigSupportedLocales().then(allowedBrowserLocales => {
      const supportedLocaleValues = supportedLocales
        .map(l => l.value)
        .filter(value => allowedBrowserLocales.includes(value));

      if (supportedLocaleValues.includes(browserLocale)) {
        setLocale(browserLocale);
      } else if (supportedLocaleValues.includes(browserLocale.split('-')[0])) {
        setLocale(browserLocale.split('-')[0]);
      } else {
        setLocale(DEFAULT_LOCALE);
      }
    });

    async function loadWLConfigSupportedLocales() {
      return firstValueFrom(loadedConfig).then(config => {
        return config.allowedBrowserLocales ?? [];
      });
    }
  }, [browserLocale, loadedConfig]);

  useEffect(() => {
    if (locale) {
      mixpanel.register({ [MixpanelMetadata.Locale]: locale });
    }
  }, [locale, mixpanel]);

  return {
    locale,
    discourageLanguageTranslation,
  };
}

function useWhitelabelIntlProviderStateWithCatalog(
  translationModules: WhitelabelTranslationModules,
  catalog: WhitelabelIntlCatalogId
) {
  const { loadedConfig } = useWLOrgConfigContext();
  const { locale, discourageLanguageTranslation } = useWhitelabelOrgLocalePreferences();
  const loadMessages = useCallback(
    (locale: string) => {
      const translationModuleKey = WHITELABEL_INTL_TRANSLATION_MODULE_KEY[catalog];
      const loadFailureDescription = `${catalog} messages`;

      return {
        messages: loadCatalogTranslations(),
        overrides: loadWLConfigTranslationOverrides(),
      };

      async function loadCatalogTranslations() {
        const key = translationModuleKey(locale);
        let langModuleFn = translationModules[key];
        if (!langModuleFn) {
          logger.error(new Error(`Failed to load ${loadFailureDescription} for ${locale}`));
          return Promise.resolve({});
        }
        return langModuleFn()
          .then(mod => mod.default)
          .catch(async error => {
            if (locale.includes('-')) {
              const language = locale.split('-')[0];
              const languageKey = translationModuleKey(language);
              langModuleFn = translationModules[languageKey];
              if (!langModuleFn) {
                logger.error(new Error(`Failed to load ${loadFailureDescription} for ${locale} (${language})`));
                throw error;
              }
              return langModuleFn()
                .then(mod => mod.default)
                .catch(err => {
                  logger.error(new Error(`Failed to load ${loadFailureDescription} for ${locale} (${language})`));
                  throw err;
                });
            }
            logger.error(new Error(`Failed to load ${loadFailureDescription} for ${locale}`));
            throw error;
          });
      }

      async function loadWLConfigTranslationOverrides() {
        return firstValueFrom(loadedConfig).then(config => {
          const translations = config.translations;
          const localeSubtag = locale.split('-')[0];
          return Promise.resolve(translations?.[locale] || translations?.[localeSubtag] || {});
        });
      }
    },
    [catalog, loadedConfig, translationModules]
  );

  return {
    locale,
    loadMessages,
    discourageLanguageTranslation,
  };
}

/**
 * `apps/whitelabel` JSON catalogs (each app’s `import.meta.glob` for `whitelabel.*.json`) plus org overrides.
 * Kyoko UI strings still come from `packages/kyoko/src/translations/kyoko.*.json` inside {@link IntlProvider}.
 */
export function useWhitelabelIntlProviderState(translationModules: WhitelabelTranslationModules) {
  return useWhitelabelIntlProviderStateWithCatalog(translationModules, WhitelabelIntlCatalog.Web);
}

/**
 * `apps/whitelabel-mobile` JSON catalogs (`whitelabel-mobile.*.json`) plus org overrides.
 * {@link IntlProvider} also loads Kyoko’s `kyoko.*.json`.
 */
export function useWhitelabelMobileIntlProviderState(translationModules: WhitelabelTranslationModules) {
  return useWhitelabelIntlProviderStateWithCatalog(translationModules, WhitelabelIntlCatalog.Mobile);
}
